/**
 * @file PermissionsContext.jsx
 * Contesto globale dei permessi utente.
 *
 * Architettura:
 *   Il backend espone GET /api/auth/permissions che ritorna un oggetto
 *   contenente SOLO le chiavi dei permessi concessi (valore true).
 *   Qualsiasi permesso non presente nell'oggetto è considerato negato.
 *
 * Flusso:
 *   1. Al login (o al cambio ruolo), il parent chiama `refreshPermissions()`.
 *   2. Il provider effettua la chiamata API e salva la risposta raw nello stato.
 *   3. I consumer usano l'hook `usePermissions()` che espone:
 *      - `permissions`: l'oggetto raw dal BE (può essere null se non ancora caricato)
 *      - `can(key)`: funzione di check con null-safety — restituisce true SOLO se
 *        il permesso esiste nell'oggetto ED è esattamente `true`.
 *      - `refreshPermissions()`: forza un ri-fetch (usato dopo login/cambio ruolo).
 *      - `isLoading`: true durante il fetch iniziale o il refresh.
 *
 * Null-safety:
 *   La funzione `can(key)` implementa il contratto richiesto:
 *     - Se permissions è null (non ancora caricato) → false
 *     - Se la chiave non esiste nell'oggetto → false
 *     - Se la chiave esiste ma il valore non è `true` → false
 *     - SOLO se la chiave esiste E il valore è `true` → true
 */

import { createContext, useState, useCallback, useRef, useEffect } from 'react';
import { getPermissions as fetchPermissions } from '../lib/api/auth';
import { useApp } from '../hooks/useApp';

// eslint-disable-next-line react-refresh/only-export-components
export const PermissionsContext = createContext(null);

/**
 * Provider dei permessi. Deve essere montato dentro AppProvider (ha bisogno
 * della sessione autenticata) e wrappare tutta l'app per rendere i permessi
 * accessibili ovunque.
 */
export function PermissionsProvider({ children }) {
  const { userRole } = useApp();
  // permissions: oggetto raw dalla risposta BE, null = non ancora caricato
  const [permissions, setPermissions] = useState(null);
  // isLoading: true durante il fetch, usato per eventuali skeleton/loading UI
  const [isLoading, setIsLoading] = useState(false);
  // Ref per evitare race condition su fetch multipli concorrenti
  const fetchIdRef = useRef(0);

  /**
   * Chiama l'API /api/auth/permissions passando il ruolo e aggiorna lo stato.
   * Chiamare dopo login, cambio ruolo, o qualunque evento che modifichi
   * i permessi dell'utente lato backend.
   *
   * @param {string} [role] - Ruolo da passare al BE. Se omesso usa il ruolo corrente dal context.
   *
   * Gestisce race condition: se vengono lanciati più refresh in rapida
   * successione, solo l'ultimo vince (tramite fetchId).
   */
  const refreshPermissions = useCallback(async (role) => {
    const effectiveRole = role ?? userRole;
    if (!effectiveRole) return;
    const currentFetchId = ++fetchIdRef.current;
    setIsLoading(true);
    try {
      const result = await fetchPermissions(effectiveRole);
      // Ignora la risposta se nel frattempo è stato lanciato un nuovo fetch
      if (currentFetchId !== fetchIdRef.current) return;
      // Se il BE ritorna null (errore), impostiamo oggetto vuoto = tutto negato
      setPermissions(result ?? {});
    } catch (err) {
      console.error('[Permissions] Errore fetch permessi:', err);
      if (currentFetchId !== fetchIdRef.current) return;
      // In caso di errore di rete, impostiamo oggetto vuoto = tutto negato (fail-closed)
      setPermissions({});
    } finally {
      if (currentFetchId === fetchIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [userRole]);

  // Auto-fetch al primo mount e ogni volta che cambia il ruolo nel context.
  // Questo copre sia il login iniziale (mount) sia il cambio ruolo da TopBar
  // (setAuth aggiorna userRole → questo effect scatta automaticamente).
  useEffect(() => {
    refreshPermissions();
  }, [refreshPermissions]);

  /**
   * can(key): verifica se un permesso specifico è concesso.
   *
   * Contratto null-safe:
   *   - permissions === null → false (non ancora caricato)
   *   - key non presente in permissions → false (negato implicitamente)
   *   - permissions[key] !== true → false (valore falsy o diverso da true)
   *   - permissions[key] === true → true (concesso esplicitamente)
   *
   * @param {string} key - Nome del permesso (es. "canEditCells", "canMerge")
   * @returns {boolean}
   */
  const can = useCallback((key) => {
    if (!permissions) return false;
    return permissions[key] === true;
  }, [permissions]);

  return (
    <PermissionsContext.Provider value={{
      permissions,
      can,
      refreshPermissions,
      isLoading,
    }}>
      {children}
    </PermissionsContext.Provider>
  );
}
